// Destination-aware downloads for Storage (cold archive).
//
// The old behaviour was one line: ask the API for a presigned URL and
// window.open() it. The browser then decided where the bytes landed — for a
// 90 GB camera master that is the wrong default, because the machine doing the
// downloading is usually an edit bay with one big scratch volume and a small
// system disk, and the browser download folder is on the small one.
//
// This module gives the caller three things the old path could not:
//
//   1. A READ OF THE DESTINATION MACHINE  — probeMachine() reports what the
//      browser will actually let us do (folder picking, secure context),
//      what the OS is, and how much room the browser thinks it has. Every
//      number here is a browser estimate, never a real statfs: the web has no
//      API for free disk space. Labels in the UI must say "estimate".
//
//   2. A READ OF THE CHOSEN FOLDER — inspectDestination() opens the picked
//      directory and reports its permission state, what is already in it, and
//      whether the incoming name collides with a file already there.
//
//   3. A STREAMED, RESUMABLE TRANSFER — startDownload() pipes the S3 response
//      straight into the chosen file, so nothing is buffered in memory, and a
//      broken transfer can resume with a Range request instead of starting a
//      90 GB file over.
//
// The File System Access API is Chromium-only and secure-context-only. Every
// entry point degrades to the plain browser download rather than throwing, and
// callers are expected to render that difference honestly rather than hide it.

const IDB_NAME  = 'heliconflow-storage-downloads';
const IDB_VER   = 1;
const IDB_STORE = 'destinations';

// How many previously used folders we offer as one-click destinations.
const MAX_REMEMBERED = 4;

// Directory listings are for orientation, not accounting. A scratch volume can
// hold tens of thousands of files and getFile() is a real syscall per entry, so
// the scan stops early and says so rather than freezing the modal.
const SCAN_ENTRY_LIMIT = 400;

// Chrome remembers the last folder per picker id, so reopening the picker
// starts where the user left off even before we restore our own handles.
const PICKER_ID = 'heliconflow-storage-destination';

// ─── Capability + machine probe ───────────────────────────────────────────────

export function canPickFolder() {
  return typeof window !== 'undefined'
    && typeof window.showDirectoryPicker === 'function'
    && window.isSecureContext === true;
}

function platformFromUa(ua) {
  if (/Windows/i.test(ua))                 return 'Windows';
  if (/Mac OS X|Macintosh/i.test(ua))      return 'macOS';
  if (/CrOS/i.test(ua))                    return 'ChromeOS';
  if (/Android/i.test(ua))                 return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua))        return 'iOS';
  if (/Linux/i.test(ua))                   return 'Linux';
  return 'Unknown';
}

function browserFromUa(ua) {
  if (/Edg\//.test(ua))                    return 'Edge';
  if (/OPR\//.test(ua))                    return 'Opera';
  if (/Firefox\//.test(ua))                return 'Firefox';
  if (/Chrome\//.test(ua))                 return 'Chrome';
  if (/Safari\//.test(ua))                 return 'Safari';
  return 'this browser';
}

// Where the browser drops a file when it is not asked. We cannot read the real
// configured path — no web API exposes it — so this is phrased as a hint and
// the UI must not present it as fact.
function defaultFolderHint(platform) {
  if (platform === 'Windows') return 'C:\\Users\\<you>\\Downloads';
  if (platform === 'macOS')   return '~/Downloads';
  if (platform === 'Linux' || platform === 'ChromeOS') return '~/Downloads';
  return 'the browser download folder';
}

export async function probeMachine() {
  const ua       = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const uaData   = (typeof navigator !== 'undefined' && navigator.userAgentData) || null;
  const platform = uaData?.platform || platformFromUa(ua);
  const browser  = browserFromUa(ua);

  // navigator.storage.estimate() reports the ORIGIN's quota and usage, not the
  // disk's. On Chromium the quota tracks a large fraction of free disk, so the
  // headroom figure moves with the volume filling up and is a useful sanity
  // check against a 90 GB file — but it is not free space and is never
  // presented as such.
  let quotaBytes = null;
  let usageBytes = null;
  try {
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      if (typeof est.quota === 'number') quotaBytes = est.quota;
      if (typeof est.usage === 'number') usageBytes = est.usage;
    }
  } catch { /* estimate is best-effort; absence is reported, not thrown */ }

  const conn = navigator.connection || navigator.webkitConnection || null;

  return {
    platform,
    browser,
    canPickFolder:  canPickFolder(),
    secureContext:  typeof window !== 'undefined' && window.isSecureContext === true,
    quotaBytes,
    usageBytes,
    headroomBytes:  quotaBytes != null && usageBytes != null ? Math.max(0, quotaBytes - usageBytes) : null,
    cores:          typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
    deviceMemoryGb: typeof navigator.deviceMemory === 'number' ? navigator.deviceMemory : null,
    downlinkMbps:   conn && typeof conn.downlink === 'number' ? conn.downlink : null,
    effectiveType:  conn?.effectiveType || null,
    saveData:       conn?.saveData === true,
    online:         navigator.onLine !== false,
    defaultFolderHint: defaultFolderHint(platform),
  };
}

// ─── Remembered destinations (IndexedDB) ──────────────────────────────────────
//
// A FileSystemDirectoryHandle is structured-cloneable, so the handle itself is
// what gets stored — not a path, which the browser never gives us. A restored
// handle comes back at permission 'prompt' and has to be re-granted from a user
// gesture; that is a browser rule, not something we can cache around.

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error || new Error('Could not open IndexedDB'));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
    tx.onabort    = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  });
}

async function readAll() {
  const db = await openDb();
  try {
    const store = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE);
    const rows  = await new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror   = () => reject(req.error);
    });
    return rows.sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0));
  } finally { db.close(); }
}

// Returns [{ key, name, handle, lastUsedAt, permission }], newest first.
// A stored handle whose directory has been deleted or unmounted still restores
// fine — it fails at queryPermission or at write time, so the permission field
// is what the UI keys off, and a hard failure is reported as 'unavailable'.
export async function loadDestinations() {
  if (!canPickFolder()) return [];
  let rows = [];
  try { rows = await readAll(); } catch { return []; }

  const out = [];
  for (const row of rows) {
    if (!row?.handle || typeof row.handle.queryPermission !== 'function') continue;
    let permission = 'prompt';
    try { permission = await row.handle.queryPermission({ mode: 'readwrite' }); }
    catch { permission = 'unavailable'; }
    out.push({ key: row.key, name: row.name || row.handle.name, handle: row.handle, lastUsedAt: row.lastUsedAt || 0, permission });
  }
  return out;
}

export async function rememberDestination(handle) {
  if (!handle) return;
  let rows = [];
  try { rows = await readAll(); } catch { return; }

  // Two different folders can share a name, so identity is isSameEntry — never
  // the display name.
  let existingKey = null;
  for (const row of rows) {
    try { if (row.handle && await row.handle.isSameEntry(handle)) { existingKey = row.key; break; } }
    catch { /* a dead handle just is not a match */ }
  }

  const key    = existingKey || `dest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const record = { key, name: handle.name, handle, lastUsedAt: Date.now() };

  const keep = rows
    .filter(r => r.key !== key)
    .sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0))
    .slice(0, MAX_REMEMBERED - 1)
    .map(r => r.key);
  const drop = rows.filter(r => r.key !== key && !keep.includes(r.key)).map(r => r.key);

  try {
    const db = await openDb();
    try {
      const tx    = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      store.put(record);
      drop.forEach(k => store.delete(k));
      await txDone(tx);
    } finally { db.close(); }
  } catch { /* remembering is a convenience; never fail a download over it */ }
}

export async function forgetDestination(key) {
  try {
    const db = await openDb();
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(key);
      await txDone(tx);
    } finally { db.close(); }
  } catch { /* nothing to do */ }
}

// ─── Picking + inspecting a destination ───────────────────────────────────────

// Resolves to null when the user dismisses the picker — a cancel is not an error.
export async function pickDestination() {
  if (!canPickFolder()) throw new Error('This browser cannot choose a download folder.');
  try {
    return await window.showDirectoryPicker({ id: PICKER_ID, mode: 'readwrite', startIn: 'downloads' });
  } catch (err) {
    if (err?.name === 'AbortError') return null;
    throw err;
  }
}

// Must be called from a user gesture — the browser rejects a permission prompt
// raised from a timer or a promise chain that has lost its activation.
export async function ensureWritePermission(handle) {
  if (!handle) return 'unavailable';
  try {
    let state = await handle.queryPermission({ mode: 'readwrite' });
    if (state !== 'granted') state = await handle.requestPermission({ mode: 'readwrite' });
    return state;
  } catch { return 'unavailable'; }
}

export function splitName(fileName) {
  const dot = String(fileName || '').lastIndexOf('.');
  if (dot <= 0) return { stem: String(fileName || ''), ext: '' };
  return { stem: fileName.slice(0, dot), ext: fileName.slice(dot) };
}

// "clip.mov" → "clip (2).mov" — the shape Chrome itself uses, so a user who has
// seen a browser download collision recognises the result.
export async function uniqueNameIn(handle, fileName) {
  const { stem, ext } = splitName(fileName);
  for (let n = 1; n < 1000; n++) {
    const candidate = n === 1 ? fileName : `${stem} (${n})${ext}`;
    // eslint-disable-next-line no-await-in-loop
    const taken = await nameExists(handle, candidate);
    if (!taken) return candidate;
  }
  return `${stem} (${Date.now()})${ext}`;
}

async function nameExists(handle, name) {
  try { await handle.getFileHandle(name); return true; }
  catch (err) {
    if (err?.name === 'NotFoundError') return false;
    // A TypeMismatchError means a DIRECTORY holds the name — still taken.
    return err?.name === 'TypeMismatchError';
  }
}

// Reads the CURRENT STATE of the chosen folder on the destination machine:
// permission, how much is already in it, and whether this exact name is already
// there (with the size and date of the file that would be replaced).
export async function inspectDestination(handle, fileName) {
  const state = {
    name: handle?.name || '—',
    permission: 'unavailable',
    scanned: false,
    files: 0,
    folders: 0,
    bytes: 0,
    truncated: false,
    collision: null,
    error: null,
  };
  if (!handle) return state;

  try { state.permission = await handle.queryPermission({ mode: 'readwrite' }); }
  catch { state.permission = 'unavailable'; return state; }

  // Without permission there is nothing to read. This is the normal state for a
  // folder restored from a previous session, not a fault.
  if (state.permission !== 'granted') return state;

  try {
    let seen = 0;
    for await (const [, entry] of handle.entries()) {
      if (seen >= SCAN_ENTRY_LIMIT) { state.truncated = true; break; }
      seen++;
      if (entry.kind === 'directory') { state.folders++; continue; }
      state.files++;
      try { const f = await entry.getFile(); state.bytes += f.size; }
      catch { /* a file we cannot stat still counts as a file */ }
    }
    state.scanned = true;
  } catch (err) {
    state.error = err?.message || 'Could not read this folder.';
  }

  if (fileName) {
    try {
      const fh = await handle.getFileHandle(fileName);
      const f  = await fh.getFile();
      state.collision = { name: fileName, size: f.size, lastModified: f.lastModified };
    } catch (err) {
      if (err?.name === 'TypeMismatchError') {
        state.collision = { name: fileName, size: null, lastModified: null, isDirectory: true };
      }
      /* NotFoundError is the good case — the name is free */
    }
  }

  return state;
}

// ─── Job registry ─────────────────────────────────────────────────────────────
//
// Jobs live outside React so a transfer survives the modal closing and the
// detail drawer closing. The dock subscribes; the modal only starts them.

let seq = 0;
const jobs = [];
const listeners = new Set();

function emit() {
  const snapshot = jobs.map(j => ({ ...j }));
  listeners.forEach(fn => { try { fn(snapshot); } catch { /* a bad listener must not stop the rest */ } });
}

export function subscribeDownloads(fn) {
  listeners.add(fn);
  fn(jobs.map(j => ({ ...j })));
  return () => { listeners.delete(fn); };
}

export function hasActiveDownloads() {
  return jobs.some(j => j.status === 'running');
}

export function dismissDownload(id) {
  const i = jobs.findIndex(j => j.id === id);
  if (i >= 0 && jobs[i].status !== 'running') { jobs.splice(i, 1); emit(); }
}

// Closing the Storage detail panel dismisses every finished toast with it
// (owner call 2026-09-02) — running downloads stay, they're still working.
export function dismissFinishedDownloads() {
  const before = jobs.length;
  for (let i = jobs.length - 1; i >= 0; i--) {
    if (jobs[i].status !== 'running') jobs.splice(i, 1);
  }
  if (jobs.length !== before) emit();
}

function update(job, patch) {
  Object.assign(job, patch);
  emit();
}

// Progress is emitted at most ~5×/sec: a 64 KB chunk loop on a fast link fires
// thousands of times a second and would re-render the dock into the ground.
const PROGRESS_INTERVAL_MS = 200;

function makeJob(fields) {
  seq += 1;
  const job = {
    id: `dl-${seq}`,
    status: 'running',
    receivedBytes: 0,
    totalBytes: null,
    rateBps: null,
    error: null,
    startedAt: Date.now(),
    resumable: false,
    ...fields,
  };
  jobs.push(job);
  emit();
  return job;
}

// The presigned URL is fetched fresh for every attempt rather than reused: a
// resume can happen long after the modal was opened, and a stale signature
// fails as a 403 mid-transfer, which is exactly the case this avoids.
async function presign(api, base, fileId) {
  const { data } = await api.get(`${base}/files/${fileId}/download`);
  if (!data?.url) throw new Error('The server did not return a download link.');
  return data;
}

// Streams into a file in the user's chosen folder.
//
// Failure semantics are deliberate and asymmetric:
//   • network/server failure → close() the writable so the partial file is
//     COMMITTED, then offer resume. A FileSystemWritableFileStream is a swap
//     file: abort() throws the bytes away, so committing is the only way a
//     resume is possible at all.
//   • user cancel → abort() and delete the part-file, because a cancel that
//     leaves a corrupt half-file in an edit bay is worse than no file.
export function startFolderDownload({ api, base, file, dirHandle, targetName, overwrite }) {
  const job = makeJob({
    fileId:     file.id,
    fileName:   targetName,
    sourceName: file.fileName,
    destName:   dirHandle.name,
    destKind:   'folder',
    totalBytes: Number(file.fileSizeBytes) || null,
    // Kept on the job so the dock can resume a failed transfer without the
    // modal — the directory handle is the only way back to that folder, and
    // re-opening the picker would make the user find it again.
    resumeWith: { api, base, file, dirHandle },
  });

  const controller = new AbortController();
  job.cancel = () => { job.canceling = true; controller.abort(); };

  (async () => {
    let writable = null;
    let createdFresh = !overwrite;
    let fileHandle = null;

    try {
      // Resume point: with overwrite we start at zero; otherwise a part-file
      // left by a previous failed attempt is picked up where it stopped.
      let startAt = 0;
      fileHandle = await dirHandle.getFileHandle(targetName, { create: true });
      if (!overwrite) {
        try { const existing = await fileHandle.getFile(); startAt = existing.size || 0; }
        catch { startAt = 0; }
      }

      const total = Number(file.fileSizeBytes) || null;
      if (total != null && startAt > total) startAt = 0;      // stale/oversized part-file: start over
      if (total != null && startAt === total && total > 0) {
        // Nothing left to fetch — the file is already complete on disk.
        update(job, { receivedBytes: total, totalBytes: total, status: 'done', finishedAt: Date.now(), alreadyComplete: true });
        return;
      }
      if (startAt > 0) createdFresh = false;

      const { url } = await presign(api, base, file.id);

      const headers = {};
      if (startAt > 0) headers.Range = `bytes=${startAt}-`;
      const res = await fetch(url, { signal: controller.signal, headers, credentials: 'omit' });

      if (!res.ok) throw new Error(`S3 refused the download (HTTP ${res.status}).`);
      // A 200 in reply to a Range request means the server ignored it — the
      // body is the WHOLE object, so the part-file must be discarded rather
      // than appended to, or the file silently doubles up.
      if (startAt > 0 && res.status !== 206) { startAt = 0; createdFresh = false; }
      if (!res.body) throw new Error('This browser did not return a readable stream.');

      const contentLength = Number(res.headers.get('Content-Length'));
      const totalBytes = total != null
        ? total
        : (Number.isFinite(contentLength) ? contentLength + startAt : null);
      update(job, { totalBytes, receivedBytes: startAt, resumedFrom: startAt || null });

      writable = await fileHandle.createWritable({ keepExistingData: startAt > 0 });
      if (startAt > 0) await writable.seek(startAt);

      const reader = res.body.getReader();
      let received  = startAt;
      let lastEmit  = 0;
      let windowAt  = Date.now();
      let windowBytes = 0;

      for (;;) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        // eslint-disable-next-line no-await-in-loop
        await writable.write(value);
        received    += value.byteLength;
        windowBytes += value.byteLength;

        const now = Date.now();
        if (now - lastEmit >= PROGRESS_INTERVAL_MS) {
          const elapsed = (now - windowAt) / 1000;
          const rateBps = elapsed > 0 ? windowBytes / elapsed : null;
          // Exponential smoothing: raw per-tick rates on a chunked stream swing
          // wildly and make the ETA unreadable.
          const smoothed = job.rateBps != null && rateBps != null
            ? job.rateBps * 0.7 + rateBps * 0.3
            : rateBps;
          update(job, { receivedBytes: received, rateBps: smoothed });
          lastEmit = now; windowAt = now; windowBytes = 0;
        }
      }

      await writable.close();
      writable = null;
      update(job, { receivedBytes: received, status: 'done', finishedAt: Date.now(), rateBps: null });
    } catch (err) {
      const canceled = job.canceling === true || err?.name === 'AbortError';

      if (canceled) {
        // Discard the swap file, then remove the part-file we created so a
        // canceled download leaves the folder exactly as it was found.
        if (writable) { try { await writable.abort(); } catch { /* already gone */ } }
        if (createdFresh) { try { await dirHandle.removeEntry(targetName); } catch { /* best effort */ } }
        update(job, { status: 'canceled', rateBps: null, finishedAt: Date.now() });
        return;
      }

      // Commit what arrived so the next attempt can resume from it.
      let committed = job.receivedBytes;
      if (writable) {
        try { await writable.close(); }
        catch { committed = 0; try { await writable.abort(); } catch { /* nothing */ } }
      }
      update(job, {
        status: 'error',
        rateBps: null,
        finishedAt: Date.now(),
        receivedBytes: committed,
        resumable: committed > 0,
        error: err?.message || 'The download failed.',
      });
    }
  })();

  return job;
}

// Restarts a failed folder download, resuming from whatever landed on disk.
// overwrite:false is what makes it a resume — the part-file's size becomes the
// Range offset. Re-granting permission first is required because the failure
// may have been the grant lapsing.
export async function resumeDownload(id) {
  const i = jobs.findIndex(j => j.id === id);
  if (i < 0) return null;
  const job = jobs[i];
  if (job.status === 'running' || !job.resumeWith) return null;

  const { api, base, file, dirHandle } = job.resumeWith;
  const perm = await ensureWritePermission(dirHandle);
  if (perm !== 'granted') {
    update(job, { error: 'That folder is no longer writable by this page. Start the download again to re-authorise it.' });
    return null;
  }

  jobs.splice(i, 1);
  emit();
  return startFolderDownload({ api, base, file, dirHandle, targetName: job.fileName, overwrite: false });
}

// The fallback path for browsers with no File System Access API, and the
// deliberate escape hatch for anyone who would rather use the browser's own
// download manager. The destination is the browser's configured folder and we
// cannot influence it or the filename — the server sets Content-Disposition.
export async function startBrowserDownload({ api, base, file }) {
  const { url } = await presign(api, base, file.id);
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
  const job = makeJob({
    fileId:     file.id,
    fileName:   file.fileName,
    sourceName: file.fileName,
    destName:   'Browser downloads',
    destKind:   'browser',
    totalBytes: Number(file.fileSizeBytes) || null,
    status:     'handed-off',
    finishedAt: Date.now(),
  });
  return job;
}

// ─── Formatting shared by the modal and the dock ──────────────────────────────

export function fmtRate(bps) {
  if (!bps || !Number.isFinite(bps) || bps <= 0) return '—';
  const mbps = bps / (1024 * 1024);
  if (mbps >= 1) return `${mbps.toFixed(1)} MB/s`;
  return `${(bps / 1024).toFixed(0)} KB/s`;
}

export function fmtEta(remainingBytes, bps) {
  if (!bps || !Number.isFinite(bps) || bps <= 0 || remainingBytes == null || remainingBytes <= 0) return '—';
  const secs = Math.round(remainingBytes / bps);
  if (secs < 60)   return `${secs}s left`;
  if (secs < 3600) return `${Math.round(secs / 60)} min left`;
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return `${h}h ${m}m left`;
}
